/* global AbortController, Blob */
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  InputAdornment,
  Rating,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Divider,
  LinearProgress,
  Tabs,
  Tab,
  Chip,
  TablePagination,
  TableFooter,
} from '@mui/material';
import {
  Star,
  ArrowBackIos,
  ArrowForwardIos,
  ExpandMore,
} from '@mui/icons-material';
import {
  Search,
  Clock,
  ChevronDown,
  BarChart2,
  MapPin,
  FileText,
  MousePointerClick,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import {
  getDashboardMetrics,
  getTurmas,
  getDashboardMemberMetrics,
  generateTurmaReport,
  generateTurmasReport,
  getResultadosFeedback,
  exportQuestionarioGiz,
} from '../services/api.js';
import { Collapse } from '@mui/material';
import PropTypes from 'prop-types';

// Default/Mock data will be replaced by API constants if needed
const DEFAULT_COLORS = ['#88C9AE', '#B5DBCB', '#1A2340', '#2C3E67'];
const STATUS_ORDER = ['Fila', 'Convocado', 'Confirmado', 'Expirado'];
const STATUS_COLORS = {
  Fila: '#F57F17',
  Convocado: '#3366FF',
  Confirmado: '#2E7D32',
  Expirado: '#C62828',
};
const STUDENT_TABLE_COLUMN_COUNT = 7;

const transformTurmasStatusData = turmas =>
  (turmas || []).map(turma => {
    const row = {
      nome: turma.nome,
      id_turma: turma.id_turma,
    };
    STATUS_ORDER.forEach(status => {
      row[status] = 0;
      row[`${status}_qtd`] = 0;
    });
    (turma.distribuicao_status || []).forEach(item => {
      if (!STATUS_ORDER.includes(item.tipo)) return;
      row[item.tipo] = item.percentual ?? 0;
      row[`${item.tipo}_qtd`] = item.quantidade ?? 0;
    });
    return row;
  });

const METRIC_LABEL_SX = {
  fontSize: '13px',
  color: '#999',
  fontWeight: 700,
  mb: 2,
};

const METRIC_VALUE_SX = {
  fontWeight: 800,
  color: '#1A2340',
  fontSize: '64px',
  lineHeight: 1,
};

const METRIC_UNIT_SX = {
  fontSize: '18px',
  color: '#999',
  fontWeight: 700,
  mt: 0.5,
};

const formatDashboardDate = value => {
  if (!value) return '—';
  const [y, m, d] = String(value).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return value;
  return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
};

const StatCard = ({ title, value, subtitle, progress, segments }) => (
  <Paper
    sx={{
      p: '28px',
      borderRadius: '20px',
      flex: 1,
      minWidth: '220px',
      boxShadow: '0 6px 24px rgba(0,0,0,0.04)',
      bgcolor: '#FFF',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    }}
  >
    <Box>
      <Typography
        sx={{
          fontWeight: 800,
          color: '#000',
          mb: 1.5,
          textTransform: 'uppercase',
          fontSize: '13px',
          letterSpacing: '0.6px',
        }}
      >
        {title}
      </Typography>
      <Box display='flex' alignItems='baseline' gap={1.5}>
        <Typography
          sx={{
            fontWeight: 800,
            color: '#1A2340',
            fontSize: '48px',
            lineHeight: 1,
          }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography
            sx={{ color: '#88C9AE', fontSize: '12px', fontWeight: 700 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>

    {segments ? (
      <Box sx={{ mt: 3 }}>
        <Box
          sx={{
            height: 12,
            width: '100%',
            bgcolor: '#F3F4F6',
            borderRadius: 6,
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          {segments.map((seg, i) => (
            <Box
              key={i}
              sx={{
                height: '100%',
                width: `${seg.value}%`,
                bgcolor: seg.color,
              }}
            />
          ))}
        </Box>
        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {segments.map((seg, i) => (
            <Box key={i} display='flex' alignItems='center' gap={0.8}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: seg.color,
                }}
              />
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 800,
                  color: seg.color,
                  textTransform: 'uppercase',
                }}
              >
                {seg.label}: {seg.value}%
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    ) : (
      progress !== undefined && (
        <Box sx={{ mt: 3 }}>
          {/* Fix: show a visible track even when progress is 0% */}
          <Box
            sx={{
              position: 'relative',
              height: 12,
              width: '100%',
              bgcolor: '#E8F5E9',
              borderRadius: 6,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                // Minimum 4px stub so 0% is still visible
                width: progress === 0 ? '4px' : `${progress}%`,
                bgcolor: progress === 0 ? '#C8E6C9' : '#66BB6A',
                borderRadius: 6,
              }}
            />
          </Box>
          <Typography
            sx={{
              mt: 1,
              display: 'block',
              textAlign: 'center',
              fontWeight: 800,
              color: progress === 0 ? '#AAA' : '#66BB6A',
              fontSize: '12px',
            }}
          >
            {progress}%
          </Typography>
        </Box>
      )
    )}
  </Paper>
);

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  subtitle: PropTypes.string,
  progress: PropTypes.number,
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    })
  ),
};

const ChartCard = ({ title, data, colors }) => (
  <Paper
    sx={{
      p: '28px',
      borderRadius: '20px',
      flex: 1,
      minWidth: { xs: '100%', sm: '220px' },
      boxShadow: '0 6px 24px rgba(0,0,0,0.04)',
      bgcolor: '#FFF',
    }}
  >
    <Typography
      sx={{
        fontWeight: 800,
        color: '#000',
        mb: 2.5,
        textTransform: 'uppercase',
        fontSize: '13px',
        letterSpacing: '0.6px',
      }}
    >
      {title}
    </Typography>
    {/* Fix 3: empty state guard so gender chart doesn't disappear */}
    {data.length === 0 ? (
      <Box
        height={130}
        display='flex'
        alignItems='center'
        justifyContent='center'
      >
        <Typography sx={{ color: '#CCC', fontSize: '13px', fontWeight: 700 }}>
          Sem dados
        </Typography>
      </Box>
    ) : (
      // Fix 2: use minHeight instead of fixed height to prevent clipping
      <Box
        sx={{
          minHeight: 130,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ width: '45%', height: 130, minWidth: 90 }}>
          <ResponsiveContainer width='100%' height='100%'>
            <PieChart>
              <Pie
                data={data}
                innerRadius={28}
                outerRadius={46}
                stroke='none'
                dataKey='value'
                startAngle={90}
                endAngle={450}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={colors[index % colors.length]}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minWidth: 80,
          }}
        >
          {data.map((entry, index) => (
            <Box key={entry.name} display='flex' alignItems='center' mb={0.5}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  bgcolor: colors[index % colors.length],
                  mr: 1,
                  flexShrink: 0,
                  borderRadius: '2px',
                }}
              />
              <Typography
                sx={{
                  color: '#666',
                  fontSize: '11px',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                {entry.name} ({entry.value}%)
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    )}
  </Paper>
);

ChartCard.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })
  ).isRequired,
  colors: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const StatusTurmasModal = ({ open, onClose, data }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ fontWeight: 800, fontSize: '20px', color: '#1A2340' }}>
      Status das Turmas
    </DialogTitle>
    <DialogContent dividers sx={{ p: { xs: 2, md: 4 } }}>
      {!data || data.length === 0 ? (
        <Typography>Nenhuma informação de turma disponível.</Typography>
      ) : (
        data.map((turma) => (
          <Accordion key={turma.id_turma} sx={{ mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography sx={{ fontWeight: 700 }}>{turma.nome}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box mb={3}>
                <Typography sx={{ fontWeight: 800, mb: 1, fontSize: '14px', color: '#1A2340' }}>Distribuição de Status</Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  {(turma.distribuicao_status || []).map((status, idx) => (
                    <Box key={idx} sx={{ p: 1.5, bgcolor: '#F9FAFB', borderRadius: '8px', minWidth: '120px' }}>
                      <Typography sx={{ fontSize: '12px', color: '#666', fontWeight: 600 }}>{status.tipo}</Typography>
                      <Typography sx={{ fontSize: '18px', fontWeight: 800, color: STATUS_COLORS[status.tipo] || '#1A2340' }}>
                        {status.quantidade} <span style={{ fontSize: '12px', color: '#999' }}>({status.percentual}%)</span>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 800, mb: 1, fontSize: '14px', color: '#1A2340' }}>Usuários ({turma.usuarios?.length || 0})</Typography>
                {turma.usuarios?.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#F9FAFB' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {turma.usuarios.map((user) => (
                          <TableRow key={user.id_usuario}>
                            <TableCell sx={{ fontSize: '13px' }}>{user.nome}</TableCell>
                            <TableCell>
                              <Chip 
                                label={user.status} 
                                size="small" 
                                sx={{ 
                                  bgcolor: STATUS_COLORS[user.status] || '#E0E0E0', 
                                  color: user.status ? '#FFF' : '#000',
                                  fontWeight: 600,
                                  fontSize: '11px'
                                }} 
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography sx={{ fontSize: '13px', color: '#666' }}>Nenhum usuário nesta turma.</Typography>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </DialogContent>
    <DialogActions sx={{ p: 3 }}>
      <Button onClick={onClose} variant="contained" sx={{ bgcolor: '#1A2340', color: '#FFF', '&:hover': { bgcolor: '#2C3E67' }, textTransform: 'none', fontWeight: 800, borderRadius: '8px' }}>
        Fechar
      </Button>
    </DialogActions>
  </Dialog>
);

StatusTurmasModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.array,
};

const TurmasStatusTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: '#FFF',
        border: '1px solid #EAEAEA',
        borderRadius: '8px',
        p: 1.5,
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      }}
    >
      <Typography
        sx={{ fontSize: '12px', fontWeight: 800, color: '#1A2340', mb: 1 }}
      >
        {label}
      </Typography>
      {payload.map(entry => (
        <Box
          key={entry.dataKey}
          display='flex'
          alignItems='center'
          gap={1}
          mb={0.5}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: entry.color,
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#666' }}>
            {entry.dataKey}: {entry.value}% (
            {entry.payload?.[`${entry.dataKey}_qtd`] ?? 0})
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

TurmasStatusTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string,
};

const TurmasStatusStackedChart = ({ data }) => {
  const chartData = useMemo(() => transformTurmasStatusData(data), [data]);
  const chartHeight = Math.max(220, chartData.length * 48);

  return (
    <Paper
      sx={{
        p: '28px',
        borderRadius: '20px',
        boxShadow: '0 6px 24px rgba(0,0,0,0.04)',
        bgcolor: '#FFF',
        width: '100%',
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          color: '#000',
          mb: 2.5,
          textTransform: 'uppercase',
          fontSize: '13px',
          letterSpacing: '0.6px',
        }}
      >
        Distribuição de Status por Turma
      </Typography>

      {chartData.length === 0 ? (
        <Box
          height={130}
          display='flex'
          alignItems='center'
          justifyContent='center'
        >
          <Typography sx={{ color: '#CCC', fontSize: '13px', fontWeight: 700 }}>
            Sem dados
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%', height: chartHeight }}>
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart
              data={chartData}
              layout='vertical'
              margin={{ top: 8, right: 24, left: 16, bottom: 8 }}
            >
              <XAxis
                type='number'
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                tick={{ fontSize: 11, fill: '#666', fontWeight: 700 }}
                axisLine={{ stroke: '#1A2340' }}
                tickLine={{ stroke: '#1A2340' }}
              />
              <YAxis
                type='category'
                dataKey='nome'
                width={160}
                tick={{ fontSize: 11, fill: '#666', fontWeight: 700 }}
                axisLine={{ stroke: '#1A2340' }}
                tickLine={false}
              />
              <Tooltip content={<TurmasStatusTooltip />} />
              <Legend
                verticalAlign='bottom'
                iconType='circle'
                wrapperStyle={{
                  fontSize: '11px',
                  fontWeight: 700,
                  paddingTop: 12,
                }}
              />
              {STATUS_ORDER.map(status => (
                <Bar
                  key={status}
                  dataKey={status}
                  stackId='status'
                  fill={STATUS_COLORS[status]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

TurmasStatusStackedChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id_turma: PropTypes.number,
      nome: PropTypes.string,
      distribuicao_status: PropTypes.arrayOf(
        PropTypes.shape({
          tipo: PropTypes.string,
          quantidade: PropTypes.number,
          percentual: PropTypes.number,
        })
      ),
    })
  ),
};

const RetentionFunnel = ({ data }) => {
  // Fix 6: only use mock data when no real data is passed at all
  const stages = data && data.length > 0 ? data : [];

  return (
    <Paper
      sx={{
        p: '32px',
        borderRadius: '20px',
        height: '100%',
        boxShadow: '0 6px 24px rgba(0,0,0,0.04)',
        bgcolor: '#FFF',
        boxSizing: 'border-box',
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          color: '#000',
          mb: 4,
          textTransform: 'uppercase',
          fontSize: '13px',
          letterSpacing: '0.6px',
        }}
      >
        Retenção de Alunos
      </Typography>

      {stages.length === 0 ? (
        <Box display='flex' alignItems='center' justifyContent='center' py={4}>
          <Typography sx={{ color: '#CCC', fontSize: '13px', fontWeight: 700 }}>
            Sem dados de retenção
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {stages.map((stage, idx) => {
            // Fix 6: bars with 0% or very small % still show a visible stub
            const barWidth = Math.max(stage.percent, 0);
            const showLabelInside = barWidth >= 20;
            return (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '36px',
                }}
              >
                {/* Bar Area */}
                <Box sx={{ flex: 1, position: 'relative', height: '24px' }}>
                  {/* Background track so 0% bar is still visible */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: '100%',
                      bgcolor: '#F0F0F0',
                      borderRadius: '4px',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: barWidth === 0 ? '4px' : `${barWidth}%`,
                      minWidth: barWidth === 0 ? '4px' : undefined,
                      height: '100%',
                      bgcolor: barWidth === 0 ? '#D0D0D0' : '#88C9AE',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'width 0.5s ease',
                      '&:hover': {
                        bgcolor: barWidth === 0 ? '#BDBDBD' : '#77BBA1',
                      },
                    }}
                  >
                    {showLabelInside && (
                      <Typography
                        sx={{
                          color: '#FFF',
                          fontSize: '11px',
                          fontWeight: 900,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {stage.value} — {stage.percent}%
                      </Typography>
                    )}
                  </Box>
                  {/* Label outside the bar for small % values */}
                  {!showLabelInside && (
                    <Typography
                      sx={{
                        position: 'absolute',
                        left:
                          barWidth === 0 ? '8px' : `calc(${barWidth}% + 8px)`,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '11px',
                        fontWeight: 900,
                        color: '#888',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {stage.value} — {stage.percent}%
                    </Typography>
                  )}
                </Box>

                {/* Label Column */}
                <Typography
                  sx={{
                    width: { xs: '100px', sm: '140px' },
                    ml: 2,
                    fontSize: '11px',
                    color: '#666',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {stage.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

RetentionFunnel.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      percent: PropTypes.number.isRequired,
    })
  ),
};

const StudentRow = ({ user }) => {
  const [open, setOpen] = useState(false);

  // Use real data from the API response
  const moduleDetails = user.detalhamento_notas?.notas_modulos || [];
  let currentLocation = user.status_atual?.localizacao_completa || 'N/A';
  if (currentLocation.includes('Módulo 0')) {
    currentLocation = user.status_atual?.aula_atual || 'N/A';
  }

  return (
    <>
      <TableRow
        onClick={() => setOpen(!open)}
        sx={{
          cursor: 'pointer',
          '&:nth-of-type(odd)': { bgcolor: '#FFF' },
          '&:nth-of-type(even)': { bgcolor: '#F9FAFB' },
          '&:hover': { bgcolor: '#F1F3F9' },
          transition: 'background-color 0.2s',
        }}
      >
        <TableCell
          sx={{
            color: '#000',
            fontSize: { xs: '13px', md: '15px' },
            py: { xs: 2, md: 3.5 },
            fontWeight: 800,
          }}
        >
          {user.nome || 'Student Name'}
        </TableCell>
        <TableCell sx={{ py: { xs: 2, md: 3.5 } }}>
          <Box
            sx={{
              px: { xs: 1.5, md: 3.5 },
              py: { xs: 0.5, md: 1 },
              borderRadius: '24px',
              display: 'inline-block',
              fontSize: { xs: '11px', md: '13px' },
              fontWeight: 800,
              color: user.status === 'Em andamento' ? '#888' : '#FFF',
              bgcolor:
                user.status === 'Alerta'
                  ? '#FF5252'
                  : user.status === 'Concluído'
                    ? '#66BB6A'
                    : '#E8E8E8',
              textAlign: 'center',
              minWidth: { xs: '100px', md: '140px' },
            }}
          >
            {user.status}
          </Box>
        </TableCell>
        <TableCell
          sx={{
            color: '#666',
            fontSize: { xs: '13px', md: '15px' },
            py: { xs: 2, md: 3.5 },
            fontWeight: 700,
          }}
        >
          {user.modalidade}
        </TableCell>
        <TableCell
          sx={{
            color: '#666',
            fontSize: { xs: '13px', md: '15px' },
            py: { xs: 2, md: 3.5 },
            fontWeight: 700,
          }}
        >
          {formatDashboardDate(user.data_inicio)}
        </TableCell>
        <TableCell
          sx={{
            color: '#666',
            fontSize: { xs: '13px', md: '15px' },
            py: { xs: 2, md: 3.5 },
            fontWeight: 700,
          }}
        >
          {formatDashboardDate(user.data_fim)}
        </TableCell>
        <TableCell sx={{ py: { xs: 2, md: 3.5 } }}>
          <Box display='flex' alignItems='center' gap={{ xs: 1, md: 3 }}>
            <Box
              sx={{
                flex: 1,
                maxWidth: { xs: '100px', md: '220px' },
                height: 10,
                bgcolor: '#E0E0E0',
                borderRadius: 6,
                overflow: 'hidden',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              <Box
                sx={{
                  width: `${user.percentual_progresso}%`,
                  height: '100%',
                  bgcolor:
                    user.percentual_progresso === 100 ? '#66BB6A' : '#88C9AE',
                  borderRadius: 6,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                color: '#000',
                fontSize: { xs: '13px', md: '15px' },
              }}
            >
              {user.percentual_progresso}%
            </Typography>
          </Box>
        </TableCell>
        <TableCell
          sx={{
            color: '#1A2340',
            fontSize: { xs: '13px', md: '15px' },
            py: { xs: 2, md: 3.5 },
            fontWeight: 800,
            textAlign: 'center',
          }}
        >
          {user.media_final}
        </TableCell>
      </TableRow>

      <TableRow sx={{ bgcolor: '#F0F2F5' }}>
        <TableCell
          sx={{
            py: 0,
            px: 0,
            borderBottom: open ? '1px solid #DDD' : 'none',
          }}
          colSpan={STUDENT_TABLE_COLUMN_COUNT}
        >
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box
              sx={{
                p: { xs: '16px 20px', md: '24px 48px' },
                position: 'relative',
              }}
            >
              {/* Course Info */}
              <Box display='flex' alignItems='flex-start' gap={1.5} mb={3}>
                <FileText
                  size={20}
                  color='#1A2340'
                  style={{ marginTop: '2px' }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: '13.5px',
                      color: '#000',
                      mb: 1,
                    }}
                  >
                    Informações do Curso:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: '#444',
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    Curso:{' '}
                    <span style={{ fontWeight: 700 }}>
                      {user.curso_nome || 'N/A'}
                    </span>
                  </Typography>
                  <Typography
                    sx={{ fontSize: '13px', color: '#444', fontWeight: 600 }}
                  >
                    Versão:{' '}
                    <span style={{ fontWeight: 700 }}>
                      {user.curso_versao || 'N/A'}
                    </span>
                  </Typography>
                </Box>
              </Box>

              {/* Module Details */}
              <Box display='flex' alignItems='flex-start' gap={1.5} mb={3}>
                <BarChart2
                  size={20}
                  color='#66BB6A'
                  style={{ marginTop: '2px' }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: '13.5px',
                      color: '#000',
                      mb: 1,
                    }}
                  >
                    Detalhamento de Notas por Módulo:
                  </Typography>
                  <Box sx={{ ml: 0 }}>
                    {moduleDetails.map((mod, i) => (
                      <Typography
                        key={i}
                        sx={{
                          fontSize: '13px',
                          color: '#444',
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        {mod.modulo}:{' '}
                        <span style={{ fontWeight: 700 }}>{mod.nota}</span>{' '}
                        <span
                          style={{
                            color: mod.status.includes('Concluído')
                              ? '#66BB6A'
                              : '#888',
                          }}
                        >
                          {mod.status}
                        </span>
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Status Section */}
              <Box display='flex' alignItems='flex-start' gap={1.5}>
                <MapPin
                  size={20}
                  color='#FF5252'
                  style={{ marginTop: '2px' }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: '13.5px',
                      color: '#000',
                      mb: 0.5,
                    }}
                  >
                    Status Atual:
                  </Typography>
                  <Typography
                    sx={{ fontSize: '13px', color: '#444', fontWeight: 700 }}
                  >
                    Onde está:{' '}
                    <span style={{ fontWeight: 600, color: '#666' }}>
                      {currentLocation}
                    </span>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const parseRespostaEntry = entryStr => {
  if (!entryStr) return { text: '', count: 1 };
  const str = String(entryStr).trim();
  const match = str.match(/^(.*?)\s*\((\d+)\)$/);
  if (match) {
    return {
      text: match[1].trim(),
      count: parseInt(match[2], 10),
    };
  }
  return {
    text: str,
    count: 1,
  };
};

const getScoreForOptionText = textStr => {
  const lower = String(textStr).toLowerCase().trim();
  if (
    lower.includes('excelente') ||
    lower.includes('muito boa') ||
    lower.includes('muito bom') ||
    lower.includes('ótimo') ||
    lower.includes('otimo') ||
    lower === '5'
  )
    return 5;
  if (
    lower.includes('péssima') ||
    lower.includes('pessima') ||
    lower.includes('muito ruim') ||
    lower === '1'
  )
    return 1;
  if (
    lower.includes('boa') ||
    lower.includes('bom') ||
    lower === '4'
  )
    return 4;
  if (
    lower.includes('regular') ||
    lower.includes('médio') ||
    lower.includes('medio') ||
    lower === '3'
  )
    return 3;
  if (
    lower.includes('ruim') ||
    lower === '2'
  )
    return 2;
  return null;
};

const getOptionColor = score => {
  switch (score) {
    case 5:
      return '#2E7D32';
    case 4:
      return '#00838F';
    case 3:
      return '#F57F17';
    case 2:
      return '#E65100';
    case 1:
      return '#C62828';
    default:
      return '#1A2340';
  }
};

const FeedbackStatsDialog = ({ open, onClose, resultadosData, loading }) => {
  const [currentModuleIdx, setCurrentModuleIdx] = useState(0);

  const allModules = useMemo(() => {
    const list = [];
    if (Array.isArray(resultadosData)) {
      resultadosData.forEach(curso => {
        (curso.modulos || []).forEach(mod => {
          list.push(mod);
        });
      });
    }
    return list;
  }, [resultadosData]);

  const currentModule = allModules[currentModuleIdx];
  const currentModuleName = currentModule?.nm_modulo || 'Sem módulo';
  const currentModuleQuestions = currentModule?.respostas || [];

  const handleNextModule = () => {
    if (allModules.length === 0) return;
    setCurrentModuleIdx(prev => (prev + 1) % allModules.length);
  };

  const handlePrevModule = () => {
    if (allModules.length === 0) return;
    setCurrentModuleIdx(
      prev => (prev - 1 + allModules.length) % allModules.length
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          p: 3,
          minHeight: '600px',
          bgcolor: '#F8F9FA',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 900,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 3,
        }}
      >
        <Box>
          <Typography variant='h5' fontWeight='900' color='#1A2340'>
            Resultados de Feedback
          </Typography>
          <Typography variant='caption' color='text.secondary' fontWeight='700'>
            ANÁLISE QUANTITATIVA E QUALITATIVA POR MÓDULO
          </Typography>
        </Box>
        {allModules.length > 1 && (
          <Box
            display='flex'
            alignItems='center'
            gap={2}
            sx={{
              bgcolor: '#FFF',
              p: '8px 16px',
              borderRadius: '16px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            }}
          >
            <IconButton
              onClick={handlePrevModule}
              size='small'
              sx={{ color: '#1A2340' }}
            >
              <ArrowBackIos fontSize='small' sx={{ ml: 0.5 }} />
            </IconButton>
            <Typography
              variant='subtitle2'
              sx={{
                maxWidth: 220,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                fontWeight: 900,
                color: '#1A2340',
              }}
            >
              {currentModuleName}
            </Typography>
            <IconButton
              onClick={handleNextModule}
              size='small'
              sx={{ color: '#1A2340' }}
            >
              <ArrowForwardIos fontSize='small' />
            </IconButton>
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ mt: 1 }}>
        {loading ? (
          <Box display='flex' justifyContent='center' p={8}>
            <CircularProgress sx={{ color: '#FFD700' }} />
          </Box>
        ) : (
          <Box display='flex' flexDirection='column' gap={2.5}>
            {currentModuleQuestions.length === 0 ? (
              <Box textAlign='center' py={10}>
                <Typography color='text.secondary' fontWeight='700'>
                  Nenhum feedback registrado para este módulo.
                </Typography>
              </Box>
            ) : (
              currentModuleQuestions.map((q, qIdx) => {
                const answersList = q.nm_respostas || [];

                let totalCount = 0;
                let totalScore = 0;
                let ratedCount = 0;

                answersList.forEach(ansStr => {
                  const match = String(ansStr).match(/^(.*?)\s*\((\d+)\)$/);
                  const text = match ? match[1].trim() : String(ansStr).trim();
                  const count = match ? parseInt(match[2], 10) : 1;
                  totalCount += count;

                  const score = getScoreForOptionText(text);
                  if (score !== null) {
                    totalScore += score * count;
                    ratedCount += count;
                  }
                });

                const avgRating =
                  ratedCount > 0 ? (totalScore / ratedCount).toFixed(1) : null;

                return (
                  <Accordion
                    key={`mod-${currentModuleIdx}-q-${qIdx}`}
                    defaultExpanded={false}
                    sx={{
                      borderRadius: '20px !important',
                      '&:before': { display: 'none' },
                      boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                      border: '1px solid #EAECEF',
                      bgcolor: '#FFF',
                      overflow: 'hidden',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: '#1A2340' }} />}
                      sx={{
                        px: 3,
                        py: 1,
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                        },
                      }}
                    >
                      <Box
                        display='flex'
                        alignItems='center'
                        justifyContent='space-between'
                        width='100%'
                        pr={2}
                      >
                        <Box sx={{ maxWidth: '65%' }}>
                          <Typography
                            color='text.secondary'
                            variant='caption'
                            fontWeight='800'
                            sx={{
                              textTransform: 'uppercase',
                              letterSpacing: '1px',
                              mb: 0.5,
                              display: 'block',
                            }}
                          >
                            {q.nr_ordem ? `Questão ${q.nr_ordem}:` : 'Questão:'}
                          </Typography>
                          <Typography
                            fontWeight='800'
                            sx={{ color: '#1A2340', fontSize: '15px' }}
                          >
                            {q.nm_questao}
                          </Typography>
                        </Box>

                        <Box display='flex' alignItems='center' gap={3}>
                          {avgRating && (
                            <Box
                              display='flex'
                              flexDirection='column'
                              alignItems='center'
                              sx={{
                                bgcolor: '#FFFBE6',
                                p: '8px 16px',
                                borderRadius: '16px',
                                border: '2px solid #FFE58F',
                              }}
                            >
                              <Box display='flex' alignItems='center' gap={1}>
                                <Star size={20} color='#fbbf24' fill='#fbbf24' />
                                <Typography
                                  fontWeight='900'
                                  fontSize='20px'
                                  color='#876800'
                                  sx={{
                                    lineHeight: 1,
                                    display: 'flex',
                                    alignItems: 'baseline',
                                  }}
                                >
                                  {avgRating}
                                  <Box
                                    component='span'
                                    sx={{
                                      fontSize: '13px',
                                      ml: 0.5,
                                      opacity: 0.7,
                                    }}
                                  >
                                    /5.0
                                  </Box>
                                </Typography>
                              </Box>
                              <Typography
                                variant='caption'
                                fontWeight='800'
                                color='#B78103'
                                sx={{ mt: 0.5 }}
                              >
                                MÉDIA
                              </Typography>
                            </Box>
                          )}
                          <Box textAlign='right'>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              fontWeight='800'
                              display='block'
                            >
                              RESPOSTAS:
                            </Typography>
                            <Typography
                              variant='h6'
                              fontWeight='900'
                              sx={{ color: '#1A2340', lineHeight: 1, mt: 0.5 }}
                            >
                              {totalCount}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ px: 3, pb: 3, pt: 1 }}>
                      <Divider sx={{ mb: 3 }} />
                      <Typography
                        variant='caption'
                        fontWeight='800'
                        color='text.secondary'
                        sx={{
                          mb: 2,
                          display: 'block',
                          textTransform: 'uppercase',
                        }}
                      >
                        Respostas registradas:
                      </Typography>

                      <Box
                        display='flex'
                        flexWrap='wrap'
                        gap={1.5}
                        sx={{ maxHeight: 300, overflowY: 'auto', p: 0.5 }}
                      >
                        {answersList.map((ansStr, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              bgcolor: '#F1F3F9',
                              p: '12px 20px',
                              borderRadius: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              border: '1px solid #E2E8F0',
                              transition: 'transform 0.2s',
                              '&:hover': { transform: 'translateY(-2px)' },
                            }}
                          >
                            <Star size={14} color='#fbbf24' fill='#fbbf24' />
                            <Typography
                              variant='body2'
                              fontWeight='800'
                              color='#1A2340'
                            >
                              {ansStr}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ p: 4, bgcolor: '#F8F9FA', borderRadius: '0 0 24px 24px' }}
      >
        <Button
          onClick={onClose}
          variant='contained'
          sx={{
            bgcolor: '#1A2340',
            color: '#FFF',
            '&:hover': { bgcolor: '#2C3E67' },
            fontWeight: 900,
            borderRadius: '16px',
            px: 6,
            py: 1.5,
            textTransform: 'none',
            fontSize: '15px',
            boxShadow: '0 4px 12px rgba(26, 35, 64, 0.2)',
          }}
        >
          Concluir Visualização
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FeedbackStatsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  resultadosData: PropTypes.array,
  loading: PropTypes.bool,
};

const MonitorDashboardPage = () => {
  const [users, setUsers] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [turmas, setTurmas] = useState([]);
  const [selectedTurma, setSelectedTurma] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'nm_usuario',
    direction: 'asc',
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [exportGizLoading, setExportGizLoading] = useState(false);
  const [statusTurmasOpen, setStatusTurmasOpen] = useState(false);

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  // Feedback State
  const [resultadosFeedback, setResultadosFeedback] = useState([]);
  const [feedbacksLoading, setFeedbacksLoading] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const fetchIdRef = useRef(0);

  const feedbackStats = useMemo(() => {
    let totalCount = 0;
    let totalScore = 0;

    (resultadosFeedback || []).forEach(curso => {
      (curso.modulos || []).forEach(mod => {
        (mod.respostas || []).forEach(q => {
          (q.nm_respostas || []).forEach(resp => {
            const { text, count } = parseRespostaEntry(resp);
            if (q.cd_tipo_questao === 'O') {
              const score = getScoreForOptionText(text);
              if (score !== null) {
                totalScore += score * count;
                totalCount += count;
              }
            }
          });
        });
      });
    });

    return {
      avg: totalCount > 0 ? Number((totalScore / totalCount).toFixed(1)) : 0,
      total: totalCount,
    };
  }, [resultadosFeedback]);

  const handleFilterClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleTurmaSelect = turmaId => {
    setSelectedTurma(turmaId);
    handleFilterClose();
  };

  const handleSort = key => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setPage(0); // Reset to first page on sort
  };

  const handleGenerateReport = async () => {
    setReportLoading(true);
    try {
      const blob = selectedTurma
        ? await generateTurmaReport(selectedTurma)
        : await generateTurmasReport();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        selectedTurma
          ? `relatorio_turma_${selectedTurma}_${new Date().getTime()}.pdf`
          : `relatorio_turmas_${new Date().getTime()}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch {
      // Handle error (optionally show a notification)
    } finally {
      setReportLoading(false);
    }
  };

  const handleExportGiz = async () => {
    setExportGizLoading(true);
    try {
      const blob = await exportQuestionarioGiz(selectedTurma);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `questionario_giz_${new Date().getTime()}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch {
      // Handle error
    } finally {
      setExportGizLoading(false);
    }
  };

  useEffect(() => {
    const fetchTurmas = async () => {
      try {
        const data = await getTurmas();
        setTurmas(data || []);
      } catch {
        // Silently fail
      }
    };
    fetchTurmas();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const fetchId = ++fetchIdRef.current;

    const fetchData = async () => {
      setMetricsLoading(true);
      setUsers([]);
      try {
        const [metricsData, membersData] = await Promise.all([
          getDashboardMetrics(selectedTurma, { signal: controller.signal }),
          getDashboardMemberMetrics(
            selectedTurma,
            page + 1,
            rowsPerPage,
            sortConfig.key,
            sortConfig.direction,
            { signal: controller.signal }
          ),
        ]);

        if (fetchId !== fetchIdRef.current) return;

        setMetrics(metricsData);
        setUsers(membersData.items || []);
        setTotalUsers(membersData.total || 0);
      } catch (err) {
        if (err.name === 'CanceledError' || err.name === 'AbortError') return;
        // Erro ao buscar dados do dashboard
      } finally {
        if (fetchId === fetchIdRef.current) {
          setLoading(false);
          setMetricsLoading(false);
        }
      }
    };
    fetchData();
    return () => controller.abort();
  }, [selectedTurma, sortConfig, page, rowsPerPage]);

  useEffect(() => {
    const fetchFeedbackData = async () => {
      setFeedbacksLoading(true);
      try {
        const data = await getResultadosFeedback(selectedTurma);
        setResultadosFeedback(data || []);
      } catch (err) {
        console.error('Erro ao carregar resultados de feedback:', err);
      } finally {
        setFeedbacksLoading(false);
      }
    };
    fetchFeedbackData();
  }, [selectedTurma]);

  // If dialog is opened after data was already fetched, don't refetch
  // (feedbackOpen state is still used only to show/hide the dialog)

  const filteredUsers = users.filter(user =>
    (user.nome || user.nm_usuario || user.name || '')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='80vh'
      >
        <CircularProgress sx={{ color: '#88C9AE' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#F8F9FA',
        minHeight: '100vh',
        // Fix 1: responsive padding
        p: { xs: '16px', sm: '24px 32px', md: '48px 64px', lg: '64px 100px' },
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Header Filters — Fix 1: responsive flex direction */}
      <Box
        display='flex'
        justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
        flexWrap='wrap'
        mb={5}
        alignItems='center'
        gap={1.5}
      >
        <Typography sx={{ fontWeight: 900, fontSize: '13px', color: '#000' }}>
          FILTRO:
        </Typography>

        <Button
          variant='outlined'
          key='turma-filter'
          onClick={handleFilterClick}
          endIcon={<ChevronDown size={18} />}
          sx={{
            borderColor: '#BBB',
            color: '#000',
            textTransform: 'none',
            px: 4,
            py: 1,
            borderRadius: '12px',
            bgcolor: '#FFF',
            fontWeight: 800,
            fontSize: '13px',
            '&:hover': { borderColor: '#888', bgcolor: '#F9F9F9' },
          }}
        >
          {selectedTurma
            ? turmas.find(t => t.id_turma === selectedTurma)?.nm_descricao ||
              'TURMA'
            : 'TODAS AS TURMAS'}
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleFilterClose}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              mt: 1,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              minWidth: '200px',
            },
          }}
        >
          <MenuItem
            onClick={() => handleTurmaSelect(null)}
            selected={selectedTurma === null}
            sx={{ fontWeight: 700, fontSize: '13px' }}
          >
            TODAS AS TURMAS
          </MenuItem>
          {turmas.map(turma => (
            <MenuItem
              key={turma.id_turma}
              onClick={() => handleTurmaSelect(turma.id_turma)}
              selected={selectedTurma === turma.id_turma}
              sx={{ fontWeight: 700, fontSize: '13px' }}
            >
              {turma.nm_descricao}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <FeedbackStatsDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        resultadosData={resultadosFeedback}
        loading={feedbacksLoading}
      />

      {/* Fix 7: inline loading overlay instead of fixed-position spinner */}
      {metricsLoading && !loading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            mb: 3,
            p: '12px 20px',
            bgcolor: '#FFF',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            width: 'fit-content',
            mx: 'auto',
          }}
        >
          <CircularProgress size={18} sx={{ color: '#88C9AE' }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#666' }}>
            Atualizando dados...
          </Typography>
        </Box>
      )}

      {/* Top row stats — Fix 1: responsive wrapping */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          mb: 5,
          width: '100%',
          // Fade out during refetch so the UI doesn't stutter
          opacity: metricsLoading ? 0.5 : 1,
          transition: 'opacity 0.3s ease',
          pointerEvents: metricsLoading ? 'none' : 'auto',
        }}
      >
        <StatCard
          title='Total de Alunos'
          value={metrics?.total_alunos ?? 0}
          subtitle={
            selectedTurma
              ? `Vagas restantes: ${metrics?.vagas_restantes ?? 0}`
              : null
          }
          segments={(metrics?.distribuicao_modalidade || []).map((m, i) => ({
            label: m.tipo,
            value: m.percentual,
            color: i === 0 ? '#88C9AE' : '#1A2340',
          }))}
        />
        <StatCard
          title='Certificados Emitidos'
          value={metrics?.total_certificados ?? 0}
          progress={metrics?.percentual_certificados ?? 0}
        />
        <ChartCard
          title='Distribuição de Gênero'
          data={(metrics?.distribuicao_genero || []).map(g => ({
            name: g.tipo,
            value: g.percentual,
          }))}
          colors={DEFAULT_COLORS}
        />
        <ChartCard
          title='Grupos de Idade'
          data={(metrics?.distribuicao_idade || []).map(a => ({
            name: a.tipo,
            value: a.percentual,
          }))}
          colors={DEFAULT_COLORS}
        />
        <ChartCard
          title='Tipos de Instituição'
          data={(metrics?.distribuicao_instituicao || []).map(i => ({
            name: i.tipo,
            value: i.percentual,
          }))}
          colors={DEFAULT_COLORS}
        />
      </Box>

      <Box
        sx={{
          mb: 5,
          width: '100%',
          opacity: metricsLoading ? 0.5 : 1,
          transition: 'opacity 0.3s ease',
          pointerEvents: metricsLoading ? 'none' : 'auto',
        }}
      >
        <TurmasStatusStackedChart
          data={metrics?.distribuicao_turmas_status || []}
        />
      </Box>

      {/* Middle section — Fix 1: responsive minWidth */}
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 5, width: '100%' }}
      >
        <Box sx={{ flex: 1.2, minWidth: { xs: '100%', md: '400px' } }}>
          <RetentionFunnel
            data={(metrics?.retencao || []).map(r => ({
              label: r.etapa,
              value: r.quantidade,
              percent: r.percentual,
            }))}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            minWidth: { xs: '100%', md: '400px' },
          }}
        >
          {/* Avaliação */}
          <Paper
            sx={{
              p: '32px',
              borderRadius: '20px',
              bgcolor: '#FFF',
              boxShadow: '0 6px 24px rgba(0,0,0,0.04)',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 3, sm: 2 },
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  mb: 1.5,
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                }}
              >
                Avaliação do Curso
              </Typography>
              <Box display='flex' alignItems='center' gap={1.5}>
                <Rating
                  value={feedbackStats.avg}
                  precision={0.1}
                  readOnly
                  sx={{ color: '#FFD700', fontSize: '32px' }}
                />
                <Typography
                  sx={{ fontWeight: 800, fontSize: '14px', color: '#999' }}
                >
                  {feedbackStats.avg}
                  <Box
                    component='span'
                    sx={{ fontSize: '11px', ml: 0.2, opacity: 0.7 }}
                  >
                    /5.0
                  </Box>{' '}
                  ({feedbackStats.total})
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'stretch',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Button
                variant='contained'
                onClick={() => setFeedbackOpen(true)}
                sx={{
                  bgcolor: '#FFD700',
                  color: '#000',
                  '&:hover': { bgcolor: '#FFC800' },
                  fontWeight: 900,
                  borderRadius: '14px',
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '14px',
                  boxShadow: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Feedback
              </Button>
            </Box>
          </Paper>

          {/* Simulador */}
          <Paper
            sx={{
              p: '40px',
              borderRadius: '20px',
              bgcolor: '#FFF',
              flexGrow: 1,
              boxShadow: '0 6px 24px rgba(0,0,0,0.04)',
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                mb: 5,
                fontSize: '13px',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
              }}
            >
              Análise do Simulador
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 3, sm: 0 },
              }}
            >
              <Box
                sx={{
                  flex: 1.2,
                  borderRight: { xs: 'none', sm: '2px solid #F5F5F5' },
                  borderBottom: { xs: '2px solid #F5F5F5', sm: 'none' },
                  pr: { xs: 0, sm: 5 },
                  pb: { xs: 3, sm: 0 },
                }}
              >
                <Typography sx={METRIC_LABEL_SX}>Tempo de simulação</Typography>
                <Box display='flex' alignItems='center' gap={3}>
                  <Clock size={48} color='#1A2340' />
                  <Typography sx={METRIC_VALUE_SX}>
                    {metrics?.tempo_simulacao_minutos
                      ? Math.round(metrics.tempo_simulacao_minutos)
                      : 0}
                  </Typography>
                </Box>
                <Typography sx={METRIC_UNIT_SX}>minutos</Typography>
              </Box>
              <Box sx={{ flex: 1, pl: { xs: 0, sm: 5 }, pt: { xs: 1, sm: 0 } }}>
                <Typography sx={METRIC_LABEL_SX}>
                  Utilização do simulador
                </Typography>
                <Box display='flex' alignItems='center' gap={3}>
                  <MousePointerClick size={48} color='#1A2340' />
                  <Typography sx={METRIC_VALUE_SX}>
                    {metrics?.utilizacao_simulador ?? 0}
                  </Typography>
                </Box>
                <Typography sx={METRIC_UNIT_SX}>acessos</Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Students Table Section Header — Fix 1: responsive wrap */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          width: '100%',
        }}
      >
        <Box display='flex' gap={1.5} alignItems='center'>
          <Button
            variant='contained'
            onClick={handleGenerateReport}
            disabled={reportLoading}
            startIcon={
              reportLoading ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                <FileText size={18} />
              )
            }
            sx={{
              bgcolor: '#1A2340',
              color: '#FFF',
              '&:hover': { bgcolor: '#2C3E67' },
              fontWeight: 800,
              borderRadius: '12px',
              px: 3,
              py: 1.2,
              textTransform: 'none',
              fontSize: '13px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}
          >
            {reportLoading ? 'Gerando...' : 'Gerar Relatório PDF'}
          </Button>
          <Button
            variant='outlined'
            onClick={handleExportGiz}
            disabled={exportGizLoading}
            startIcon={
              exportGizLoading ? (
                <CircularProgress size={18} color='inherit' />
              ) : (
                <FileText size={18} />
              )
            }
            sx={{
              borderColor: '#1A2340',
              color: '#1A2340',
              '&:hover': { borderColor: '#2C3E67', bgcolor: '#F5F5F5' },
              fontWeight: 800,
              borderRadius: '12px',
              px: 3,
              py: 1.2,
              textTransform: 'none',
              fontSize: '13px',
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              '&.Mui-disabled': {
                borderColor: '#CCC',
                color: '#999',
              },
            }}
          >
            {exportGizLoading ? 'Exportando...' : 'Exportar GIZ'}
          </Button>
          <Button
            variant='outlined'
            onClick={() => setStatusTurmasOpen(true)}
            startIcon={<BarChart2 size={18} />}
            sx={{
              borderColor: '#1A2340',
              color: '#1A2340',
              '&:hover': { borderColor: '#2C3E67', bgcolor: '#F5F5F5' },
              fontWeight: 800,
              borderRadius: '12px',
              px: 3,
              py: 1.2,
              textTransform: 'none',
              fontSize: '13px',
              boxShadow: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Status Turmas
          </Button>
        </Box>

        <TextField
          placeholder='Pesquisar por nome'
          variant='outlined'
          size='small'
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          sx={{
            width: { xs: '100%', sm: 320 },
            '& .MuiOutlinedInput-root': {
              borderRadius: '24px',
              bgcolor: '#FFF',
              fontSize: '13px',
              height: 48,
              boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
              '& fieldset': { borderColor: '#DDD' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search size={18} color='#999' sx={{ ml: 1 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Students Table — Fix 1: horizontal scroll on mobile */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: '20px',
          boxShadow: '0 6px 24px rgba(0,0,0,0.04)',
          overflowX: 'auto',
          bgcolor: '#FFF',
          width: '100%',
        }}
      >
        <Table size='medium' sx={{ minWidth: 700 }}>
          <TableHead sx={{ bgcolor: '#F1F3F9' }}>
            <TableRow>
              {[
                { label: 'NOME', key: 'nm_usuario', sortable: true },
                { label: 'STATUS', key: 'status', sortable: true },
                { label: 'MODALIDADE', key: 'modalidade', sortable: false },
                { label: 'DATA INÍCIO', key: 'data_inicio', sortable: true },
                { label: 'DATA FIM', key: 'data_fim', sortable: true },
                {
                  label: 'BARRA DE PROGRESSO %',
                  key: 'percentual_progresso',
                  sortable: true,
                },
                { label: 'MÉDIA FINAL', key: 'media_final', sortable: true },
              ].map(column => (
                <TableCell
                  key={column.label}
                  onClick={() => column.sortable && handleSort(column.key)}
                  sx={{
                    fontWeight: 900,
                    color: '#1A2340',
                    fontSize: '12px',
                    py: 3,
                    letterSpacing: '0.5px',
                    cursor: column.sortable ? 'pointer' : 'default',
                    '&:hover': column.sortable ? { bgcolor: '#E8EBF3' } : {},
                  }}
                >
                  <Box display='flex' alignItems='center' gap={0.8}>
                    {column.label}
                    {column.sortable && (
                      <ChevronDown
                        size={18}
                        style={{
                          transform:
                            sortConfig.key === column.key &&
                            sortConfig.direction === 'desc'
                              ? 'rotate(180deg)'
                              : 'none',
                          transition: 'transform 0.2s',
                          color:
                            sortConfig.key === column.key ? '#1A2340' : '#AAA',
                        }}
                      />
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user, idx) => (
              <StudentRow
                key={`${user.id_usuario}-${user.modalidade}-${user.data_inicio}-${idx}`}
                user={user}
              />
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                colSpan={7}
                count={totalUsers}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Alunos por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`}
                sx={{
                  borderBottom: 'none',
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    fontWeight: 700,
                    color: '#666',
                    fontSize: '13px',
                    mt: 0,
                  },
                }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 3, height: 1 }} />

      <StatusTurmasModal
        open={statusTurmasOpen}
        onClose={() => setStatusTurmasOpen(false)}
        data={metrics?.distribuicao_turmas_status}
      />
    </Box>
  );
};

export default MonitorDashboardPage;
